import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
import { handleGetRequest } from "../../../services/GetTemplate";
import { addScannedPackage, removeScannedPackage, nextStep } from "../../../redux/routeFLowSlice";
import styles from "./ScanStep.module.scss";

function ScanStep({ deliveryId }) {
    const dispatch = useDispatch();
    const scannedPackages = useSelector((state) => state.routeFlowSlice.scannedPackages);
    const [manualWaybill, setManualWaybill] = useState("");
    const [isScanning, setIsScanning] = useState(false);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        // Focus on input when component mounts
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const fetchPackageByWaybill = async (waybill) => {
        try {
            setLoading(true);
            const response = await handleGetRequest(`/getPackageByWaybill?waybill=${waybill}`);

            if (response?.success && response?.data) {
                const packageData = response.data;

                // Check if already scanned
                const alreadyScanned = scannedPackages.find((pkg) => pkg.Waybill === packageData.Waybill);

                if (alreadyScanned) {
                    toast.warning("Package already scanned!");
                    return;
                }

                if (!packageData?.name?.latitude || !packageData?.name?.longitude) {
                    toast.error(`Package ${packageData.Waybill} has invalid location data!`);
                    return;
                }

                dispatch(addScannedPackage(packageData));
                toast.success(`Package ${packageData.Waybill} added successfully!`);
                setManualWaybill("");

                // Refocus input
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            } else {
                toast.error(`Package with waybill ${waybill} not found!`);
            }
        } catch (error) {
            console.error("Error fetching package:", error);
            toast.error("Failed to fetch package. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleManualScan = (e) => {
        e.preventDefault();
        if (manualWaybill.trim()) {
            fetchPackageByWaybill(manualWaybill.trim());
        }
    };

    const handleBarcodeScanned = (err, result) => {
        if (result) {
            const waybill = result.text;
            fetchPackageByWaybill(waybill);
        }
    };

    const handleRemovePackage = (waybill) => {
        confirmDialog({
            message: `Are you sure you want to remove package ${waybill}?`,
            header: "Confirm Removal",
            icon: "pi pi-exclamation-triangle",
            accept: () => {
                dispatch(removeScannedPackage(waybill));
                toast.info(`Package ${waybill} removed`);
            },
        });
    };

    const handleNext = () => {
        if (scannedPackages.length === 0) {
            toast.error("Please scan at least one package to continue");
            return;
        }
        dispatch(nextStep());
    };

    const actionTemplate = (rowData) => {
        return <Button icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text" onClick={() => handleRemovePackage(rowData.Waybill)} tooltip="Remove package" tooltipOptions={{ position: "top" }} />;
    };

    const clientTemplate = (rowData) => {
        return `${rowData.name?.firstName || ""} ${rowData.name?.lastName || ""}`;
    };

    return (
        <div className={styles.scanStepContainer}>
            <ConfirmDialog />

            <div className={styles.header}>
                <h2>Scan Packages</h2>
                <p>Scan package barcodes to add them to this delivery</p>
            </div>

            {/* Scanner Section */}
            <div className={styles.scannerSection}>
                <div className={styles.scanOptions}>
                    {/* Manual Input */}
                    <div className={styles.manualInput}>
                        <form onSubmit={handleManualScan} className={styles.inputForm}>
                            <InputText ref={inputRef} value={manualWaybill} onChange={(e) => setManualWaybill(e.target.value)} placeholder="Enter Waybill number..." className={styles.waybillInput} disabled={loading} />
                            <Button label="Add" icon="pi pi-plus" type="submit" disabled={!manualWaybill.trim() || loading} loading={loading} />
                        </form>
                        <small className={styles.hint}>Tip: Scan barcode or type waybill manually</small>
                    </div>

                    {/* Camera Scanner Toggle */}
                    <div className={styles.cameraToggle}>
                        <Button label={isScanning ? "Stop Camera" : "Use Camera Scanner"} icon={isScanning ? "pi pi-times" : "pi pi-camera"} onClick={() => setIsScanning(!isScanning)} className="p-button-outlined" />
                    </div>
                </div>

                {/* Camera Scanner */}
                {isScanning && (
                    <div className={styles.cameraContainer}>
                        <BarcodeScannerComponent width="100%" height={400} onUpdate={handleBarcodeScanned} />
                    </div>
                )}
            </div>

            {/* Scanned Packages Table */}
            <div className={styles.packagesSection}>
                <div className={styles.sectionHeader}>
                    <h3>Scanned Packages ({scannedPackages.length})</h3>
                </div>

                {scannedPackages.length === 0 ? (
                    <div className={styles.emptyState}>
                        <i className="pi pi-inbox" style={{ fontSize: "3rem", color: "#ccc" }}></i>
                        <p>No packages scanned yet</p>
                        <small>Start scanning to add packages</small>
                    </div>
                ) : (
                    <DataTable value={scannedPackages} responsiveLayout="scroll" className={styles.dataTable} stripedRows>
                        <Column field="Waybill" header="Waybill" sortable />
                        <Column field="name" header="Client" body={clientTemplate} sortable />
                        <Column field="description" header="Description" />
                        <Column field="weight.number" header="Weight" body={(row) => `${row.weight?.number || 0} ${row.weight?.unit || ""}`} />
                        <Column header="Actions" body={actionTemplate} style={{ width: "100px", textAlign: "center" }} />
                    </DataTable>
                )}
            </div>

            {/* Navigation */}
            <div className={styles.navigation}>
                <div className={styles.info}>
                    <i className="pi pi-info-circle"></i>
                    <span>Total Packages: {scannedPackages.length}</span>
                </div>
                <Button label="Next: Create Stops" icon="pi pi-arrow-right" iconPos="right" onClick={handleNext} disabled={scannedPackages.length === 0} />
            </div>
        </div>
    );
}

export default ScanStep;
